<template>
    <kwik-logo
    class="kwik-logo prevent-auto-margins"
    :class="{'mobile': getMobileAppState }"/>
    <!-- Title -->
    <text-block-generic
    :params="{
        content: t('transfers.kwik.accountManagement.titleText')
    }"/>
    <divider-generic style="padding-top: 0px;"/>
    <text-block-generic
    :params="{
        content: t('transfers.kwik.accountManagement.selectAccount')
    }"/>
    <divider-generic
    icon="account-balance"
    :applyBorder="false"
    :title="t('transfers.kwik.accountManagement.accounts')"/>
    <!-- Account Dropdown Selector -->
	<dropdown-account
	:params="{ labelText: t('transfers.kwik.accountManagement.account') }"
	:options="getAccountsList"
	:selectedOption="formData.originAccount"
	@clicked="originAccountDropdownClickHandler"/>
    <button-secondary
    icon="search"
    buttonSize="small"
    :disabled="preventSearch"
    :label="t('transfers.kwik.accountManagement.search')"
    @clicked="searchHandler"/>

    <!-- Empty-state -->
    <empty-state-generic
    v-if="!getAccountAlias.length && showContent"
    hasButton
    illustration="emptyState/search"
    :title="t('transfers.kwik.accountManagement.aliasEmptyState.title')"
    :description="t('transfers.kwik.accountManagement.aliasEmptyState.description')"
    :buttonLabel="t('transfers.kwik.accountManagement.aliasEmptyState.button')"
    @clicked="emit('goToStep1')"/>

    <!-- Aliases Content -->
    <div
    v-if="getAccountAlias.length && showContent"
    class="aliases-content-wrapper">
        <divider-generic
        icon="info"
        :title="t('transfers.kwik.accountManagement.regist')"/>
        <infobox-generic :label="t('transfers.kwik.accountManagement.disclaimer')"/>
        <!-- Alias Table -->
        <div class="alias-container">
            <div class="container-label">{{ t('transfers.kwik.accountManagement.aliasTableLabel') }}</div>
            <!-- Phone Row -->
            <alias-row
            canBeDefault
            aliasType="PN"
            :aliasList="getAccountAlias"
            :aliasTypeLabel="t('transfers.kwik.accountManagement.phoneLabel')"
            @validateKeyHandler="validateKeyHandler"
            @changeDefaultAliasTypeHandler="[changeAliasTypeHandler($event), showDefaultModal = true]"
            @removeAliasTypeHandler="[changeAliasTypeHandler($event), showRemoveModal = true]"
            @changeAliasTypeHandler="changeAliasTypeContactValidator($event)"/>
            <!-- Email Row -->
            <alias-row
            canBeDefault
            aliasType="EM"
            :aliasList="getAccountAlias"
            :aliasTypeLabel="t('transfers.kwik.accountManagement.emailLabel')"
            @validateKeyHandler="validateKeyHandler"
            @changeDefaultAliasTypeHandler="[changeAliasTypeHandler($event), showDefaultModal = true]"
            @removeAliasTypeHandler="[changeAliasTypeHandler($event), showRemoveModal = true]"
            @changeAliasTypeHandler="changeAliasTypeContactValidator($event)"/>
            <!-- Nickname Row -->
            <alias-row
            aliasType="NN"
            :aliasList="getAccountAlias"
            :aliasTypeLabel="t('transfers.kwik.accountManagement.nicknameLabel')"
            @validateKeyHandler="validateKeyHandler"
            @changeDefaultAliasTypeHandler="[changeAliasTypeHandler($event), showDefaultModal = true]"
            @removeAliasTypeHandler="[changeAliasTypeHandler($event), showRemoveModal = true]"
            @changeAliasTypeHandler="changeAliasTypeContactValidator($event)"/>
        </div>
    </div>

    <!-- Remove Modal -->
    <modal-centered-generic
    v-if="showRemoveModal"
    negative
    :title="t('transfers.kwik.accountManagement.remove')"
    :text="t('transfers.kwik.accountManagement.removeModalText')"
    :leftButtonText="t('transfers.kwik.accountManagement.no')"
    :rightButtonText="t('transfers.kwik.accountManagement.yesRemove')"
    rightButtonIcon="check-line"
    @hideMe="showRemoveModal = false"
    @rightButtonClicked="removeHandler"/>

    <!-- Default Modal -->
    <modal-centered-generic
    v-if="showDefaultModal"
    titleIcon="info"
    :title="t('transfers.kwik.accountManagement.defaultModalTitle')"
    :text="t('transfers.kwik.accountManagement.defaultModalText', {
        alias: getAccountAlias.find(el => el.aliasType === selectedAlias).alias,
        account: accountFormatHelper(formData.accountDisplayInfo)
    })"
    :leftButtonText="t('transfers.kwik.accountManagement.no')"
    :rightButtonText="t('transfers.kwik.accountManagement.yesDefine')"
    rightButtonIcon="check-line"
    @hideMe="showDefaultModal = false"
    @rightButtonClicked="makeDefault"/>

    <!-- No Phone Modal -->
    <modal-centered-generic
    v-if="showPhoneModal"
    titleIcon="info"
    :title="t('transfers.kwik.accountManagement.noPhoneModalTitle')"
    :text="t('transfers.kwik.accountManagement.noPhoneModalText')"
    :leftButtonText="t('transfers.kwik.accountManagement.ok')"
    leftButtonIcon="check-line"
    @hideMe="showPhoneModal = false"
    @leftButtonClicked="showPhoneModal = false"/>
    <!-- No Email Modal -->
    <modal-centered-generic
    v-if="showNoEmailModal"
    titleIcon="info"
    :title="t('transfers.kwik.accountManagement.noEmailModalTitle')"
    :text="t('transfers.kwik.accountManagement.noEmailModalText')"
    :leftButtonText="t('transfers.kwik.accountManagement.ok')"
    leftButtonIcon="check-line"
    @hideMe="showNoEmailModal = false"
    @leftButtonClicked="showNoEmailModal = false"/>
    <!-- Add Key Modal -->
    <modal-centered-generic
    v-if="showAddKeyModal"
    titleIcon="key"
    :title="t('transfers.kwik.accountManagement.addKeyModalTitle')"
    :leftButtonText="t('transfers.kwik.accountManagement.cancel')"
    :rightButtonText="t('transfers.kwik.accountManagement.addKeyBtn')"
    rightButtonIcon="check-line"
    @hideMe="[showAddKeyModal = false, clearFieldError()]"
    @rightButtonClicked="validateNickname">
        <!-- Phone Block -->
        <div v-if="selectedAlias === 'PN'" class="block">
            <text-block-generic
            :params="{
                label: t('transfers.kwik.accountManagement.phoneLabel'),
                content: formData.alias
            }"/>
            <infobox-generic
            :label="t('transfers.kwik.accountManagement.phoneDisclaimer')"/>
        </div>
        <!-- Email Block -->
        <div v-if="selectedAlias === 'EM'" class="block">
            <text-block-generic
            :params="{
                label: t('transfers.kwik.accountManagement.emailLabel'),
                content: formData.alias
            }"/>
            <infobox-generic
            :label="t('transfers.kwik.accountManagement.emailDisclaimer')"/>
        </div>
        <!-- Nickname Block -->
        <div v-if="selectedAlias === 'NN'" class="block">
            <input-generic
            :params="nicknameParams"
            :inputValue="formData.alias"
            @input="[emit('updateFormData', { fieldName: 'alias', inputValue: $event }), clearFieldError()]"/>
            <infobox-generic
            :label="t('transfers.kwik.accountManagement.nicknameDisclaimer')"/>
            <selector-radio-group
            :label="t('transfers.kwik.accountManagement.strongFactorLabel')"
            :mandatory="true"
            :radioParams="radioParams"
            @clicked="emit('updateFormData', { fieldName: 'authMethodType', inputValue: $event })"/>
            <text-block-generic
            v-if="formData.authMethodType === 'PN'"
            :params="{
                label: t('transfers.kwik.accountManagement.phoneLabel'),
                content: getAccountAliasSMSPhoneNumber
            }"/>
            <text-block-generic
            v-else
            :params="{
                label: t('transfers.kwik.accountManagement.emailLabel'),
                content: customerInfo.customer.email
            }"/>
        </div>
    </modal-centered-generic>

    <!-- Feedback Toasts -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import AliasRow from '@/components/features/transfers/kwik/accountManagement/AliasRow.vue'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import SelectorRadioGroup from '@/components/generic/selectors/radio/RadioGroup'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, onMounted, defineAsyncComponent } from 'vue'

/* Aux */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { removeAccents, noSpecialCharNoSpace } from '@/assets/scripts/validators'
import { accountFormatHelper } from '@/assets/scripts/strings'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateFormData', 'showOverlayLoader', 'hideOverlayLoader', 'goToStep1', 'tokenTitle'])

/* mapActions & mapGetters */
const {
    requestAccountAssociatedAlias, deleteAccountAssociation,
    editsAccountDefaultAliasAssociation, addAccountAliasAssociation, resendOTPForAccountAliasAssociation
} = mapActions(moduleMapper.TRANSFERS_KWIK)
const { getAccountAlias, getAccountAliasSMSPhoneNumber } = mapGetters(moduleMapper.TRANSFERS_KWIK)
const { getAppThemeMode, getMobileAppState } = mapGetters()

/** Props
@formData {Object} Object with info for our current form
@getAccountsList {Object} Object with accounts to populate dropdowns
@customerInfo {Object} Object with the customer info
@searchInfoForSelectedAccount {Boolean} Boolean to trigger search for selected account
*/
const props = defineProps({
	formData: Object,
	getAccountsList: Object,
    customerInfo: Object,
    searchInfoForSelectedAccount: Boolean
})

const kwikLogo = computed(() => {
    const iconPath = getAppThemeMode.value === 'dark' ? '-dark' : ''
	return defineAsyncComponent(() => import('@/assets/images/logo-kwik' + iconPath + '.svg?inline'))
})

const nicknameParams = ref({
	labelText: t('transfers.kwik.accountManagement.nicknameLabel'),
	placeholder: t('transfers.kwik.accountManagement.nicknamePlaceholder'),
	mandatory: true,
	maxLength: 30,
    ourMask: {
        parseInputText: value => { return removeAccents(value) },
        validatorFn: value => { return noSpecialCharNoSpace(value) }
    },
	errorState: false,
	errorMessage: null
})

const radioParams = computed(() => {
    return [{
        id: 'PN',
        text: t('transfers.kwik.accountManagement.phoneLabel'),
        value: 'PN',
        selected: props.formData.authMethodType === 'PN',
        disabled: !getAccountAliasSMSPhoneNumber.value
    }, {
        id: 'EM',
        text: t('transfers.kwik.accountManagement.emailLabel'),
        value: 'EM',
        selected: props.formData.authMethodType === 'EM',
        disabled: !props.customerInfo.customer.email
    }]
})

/*************************************************
*              Origin Account Data               *
*************************************************/
const originAccountDropdownClickHandler = event => {
	const accountDisplayInfo = props.getAccountsList.find(account => account.id === event).accountNumber

	emit('updateFormData', { fieldName: 'accountDisplayInfo', inputValue: accountDisplayInfo })
	emit('updateFormData', { fieldName: 'originAccount', inputValue: event })

    preventSearch.value = false
    showContent.value = false
}

const preventSearch = ref(false)
const showContent = ref(false)
const searchHandler = async () => {
    emit('showOverlayLoader')

    const payload = {
        accountId: props.formData.originAccount,
        params: { showPending: true }
    }

    try {
        await requestAccountAssociatedAlias(payload)

        preventSearch.value = true
        showContent.value = true
        emit('hideOverlayLoader')
    } catch (error) {
        if (error.toString() === 'Cancel') return

        if (error.response.data.code === 'associated_entity_contact_not_found') {
            emit('hideOverlayLoader')

            feedbackToastData.value = {
                toastType: 'negative',
                text: error.response.data.message,
                buttons: [{
                    text: t('commonButtons.gotIt'),
                    callbackAction: () => { showFeedbackToast.value = false }
                }]
            }
            showFeedbackToast.value = true
        } else errorHandler(error.response.data, searchHandler)
    }
}

/*************************************************
*                    Handlers                    *
*************************************************/
const selectedAlias = ref('')
const showRemoveModal = ref(false)
const showDefaultModal = ref(false)
const showPhoneModal = ref(false)
const showNoEmailModal = ref(false)
const showAddKeyModal = ref(false)

const changeAliasTypeContactValidator = $event => {
    if ($event === 'PN' && !getAccountAliasSMSPhoneNumber.value) {
        showPhoneModal.value = true
        return
    }

    if ($event === 'EM' && !props.customerInfo.customer.email) {
        showNoEmailModal.value = true
        return
    }

    changeAliasTypeHandler($event)
    showAddKeyModal.value = true
}

const changeAliasTypeHandler = (event) => {
    selectedAlias.value = event

    switch (event) {
        case 'PN':
            emit('updateFormData', { fieldName: 'authMethodType', inputValue: event })
            emit('updateFormData', { fieldName: 'alias', inputValue: getAccountAliasSMSPhoneNumber.value })
            break
        case 'EM':
            emit('updateFormData', { fieldName: 'authMethodType', inputValue: event })
            emit('updateFormData', { fieldName: 'alias', inputValue: props.customerInfo.customer.email ? props.customerInfo.customer.email : '' })
            break
        case 'NN':
            emit('updateFormData', { fieldName: 'authMethodType', inputValue: getAccountAliasSMSPhoneNumber.value ? 'PN' : 'EM' })
            emit('updateFormData', { fieldName: 'alias', inputValue: '' })
            break
    }
}

const removeHandler = async () => {
    showRemoveModal.value = false
    emit('showOverlayLoader')

	const payload = {
        accountId: props.formData.originAccount,
        params: {
            aliasType: selectedAlias.value
        }
	}

	try {
        await deleteAccountAssociation(payload)
        emit('hideOverlayLoader')
        emit('tokenTitle', t('transfers.kwik.accountManagement.removeTokenTitle'))
    } catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, removeHandler)
	}
}

const makeDefault = async () => {
    showDefaultModal.value = false
    emit('showOverlayLoader')

	const payload = {
        accountId: props.formData.originAccount,
        params: {
            aliasType: selectedAlias.value
        }
	}

	try {
        await editsAccountDefaultAliasAssociation(payload)
        emit('hideOverlayLoader')
        emit('tokenTitle', t('transfers.kwik.accountManagement.defaultTokenTitle'))
    } catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, makeDefault)
	}
}

const validateNickname = () => {
    if (selectedAlias.value === 'NN' && !props.formData.alias) {
        nicknameParams.value.errorState = true
        nicknameParams.value.errorMessage = t('mandatoryFieldError')
    } else addKeyHandler()
}

const addKeyHandler = async () => {
    showAddKeyModal.value = false
    emit('showOverlayLoader')
    emit('tokenTitle', t('transfers.kwik.accountManagement.addTokenTitle'))

	const payload = {
        accountId: props.formData.originAccount,
        params: {
            aliasType: selectedAlias.value,
            authMethodType: props.formData.authMethodType
        }
	}

    if (selectedAlias.value === 'NN') payload.params.alias = props.formData.alias

	try {
        await addAccountAliasAssociation(payload)
        emit('hideOverlayLoader')
    } catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, addKeyHandler)
	}
}

const validateKeyHandler = async event => {
    emit('showOverlayLoader')
    emit('tokenTitle', t('transfers.kwik.accountManagement.appendTokenTitle'))
    emit('updateFormData', { fieldName: 'authMethodType', inputValue: event })

	try {
        await resendOTPForAccountAliasAssociation(props.formData.originAccount)
        emit('hideOverlayLoader')
    } catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, addKeyHandler)
	}
}

const clearFieldError = () => {
	nicknameParams.value.errorMessage = null
	nicknameParams.value.errorState = false
}
/*************************************************
*             Success/Error Handlers             *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    emit('hideOverlayLoader')

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

onMounted(() => {
    if (props.searchInfoForSelectedAccount) searchHandler()
})
</script>

<style lang="scss" scoped>
.kwik-logo {
    margin-top: 24px;
    flex-shrink: 0;

    &.mobile {
        margin-left: 16px;
    }
}

.aliases-content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;

    .alias-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        // gap: 16px;

        padding: 16px;
        border-radius: 8px;
        background-color: var(--secondary-light-3);

        .line {
            width: 100%;
            height: 1px;
            background-color: var(--secondary-light-1);
        }

        .container-label {
            @include H7-Medium;
            color: var(--secondary-dark-2);
        }
    }
}

.block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;

    padding-top: 16px;
}
</style>
